import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import styled from 'styled-components';
import Table from 'core/components/HTMLTable';
import { Card } from 'core/components';
import CodeMirror from 'core/components/CodeMirror';
import MermaidDiagram from 'core/components/MermaidDiagram';
import DeviceConfigView, { isDeviceConfigLanguage } from 'app/components/device/DeviceConfigView';

const AnchorComponent = ({ node, href, children, ...props }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

const TableComponent = ({ node, ...props }) => (
  <Card elevation={1} mb={2}>
    <Table {...props} style={{ width: '100%' }} interactive bordered striped>
      {props.children}
    </Table>
  </Card>
);

const CodeBlockComponent = ({ children, ...props }) => {
  const textContent = (children?.props?.children || '').trim();
  const className = props.node.children[0]?.properties?.className || [];
  const language = className.length > 0 ? className[0].replace('language-', '') : 'text/plain';

  // Render mermaid diagrams
  if (language === 'mermaid') {
    return <MermaidDiagram chart={textContent} />;
  }

  // for supported syntax highlighting configs (diff, junos, arista, etc...)
  if (isDeviceConfigLanguage(language)) {
    return <DeviceConfigView configText={textContent} syntax={language} />;
  }

  return (
    <CodeMirror
      mode={language}
      value={textContent}
      options={{
        readOnly: true
      }}
    />
  );
};

// align center is the default, but leaving this here in case Dave changes his mind.
export const MarkdownWrap = styled.div`
  font-size: 14px;

  &.smaller {
    font-size: 12px;
  }

  .katex-display > .katex {
    text-align: center;
  }

  .CodeMirror-wrap {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .cm-editor {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .mermaid {
    display: flex;
    justify-content: center;
    margin: 16px 0;

    svg {
      max-width: 100%;
      height: auto;
    }
  }

  line-height: 1.7 !important;

  strong {
    font-weight: 700;
  }

  code {
    font-family: ${({ theme }) => theme.fonts.monospace};
    background: #4e4a67;
    font-size: 12px;
    font-weight: 500;
    padding: 3px 6px;
    border-radius: 4px;
    color: #ffffff;
  }

  p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ol,
  ul {
    margin-bottom: 16px;
    padding-left: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    li {
      margin-bottom: 8px;

      ul {
        margin-top: 6px;
        margin-bottom: 6px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  blockquote {
    font-size: 11px;
    margin: 8px 0px;
    padding: 2px 12px;
    border-left: 2px solid ${({ theme }) => theme.borderColors.thin};
  }

  hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 2rem 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 700;
  }

  h1 {
    font-size: 20px;
  }

  h2 {
    font-size: 18px;
  }

  h3 {
    font-size: 16px;
  }

  h4,
  h5 {
    font-size: 14px;
  }

  h6 {
    font-size: 12px;
  }
`;

class Markdown extends React.Component {
  render() {
    const { className, children } = this.props;
    return (
      <MarkdownWrap className={className}>
        <ReactMarkdown
          components={{
            a: AnchorComponent,
            table: TableComponent,
            pre: CodeBlockComponent
          }}
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
        >
          {children}
        </ReactMarkdown>
      </MarkdownWrap>
    );
  }
}

export default Markdown;
